// assets/src/index.js
import $ from 'jquery';
import { initDropdownToggles } from './components/DropdownToggle.js';
import { HashRouter }   from './utils/HashRouter';
import { BookingForm, NaluBookingUx }  from './components/BookingForm';
import { ManageReservation } from './components/ManageReservation.js';
import { refreshNonce } from './api/nonce';

//import { initStickySidebar } from './components/StickySidebar.js';
//document.addEventListener('DOMContentLoaded', initStickySidebar);

// Gated diagnostic (mirrors naluDebug in the components) — only logs when
// WP_DEBUG mirrored into naluBookingVars.debug is on.
const naluDebug = (...args) => {
  if (typeof window !== 'undefined' && window.naluBookingVars?.debug) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

// ────────────────────────────────────────────────────────────────
// Lazy-load state
//
// The modal markup is no longer embedded in every page footer — it's fetched
// once, on first trigger interaction (or when the page loads with ?manageres=),
// then injected and booted. `bootBooking()` is the one-time mount that the old
// DOM-ready handler used to run inline; it now runs only once the markup exists
// in the DOM (either lazily fetched, or rendered inline by the [nalu_booking]
// shortcode).
// ────────────────────────────────────────────────────────────────
let booted        = false;
let markupPromise = null; // single in-flight/settled fetch; guards double-fetch

function bootBooking () {
  if (booted) return true;

  /* 1. Find root — bail if the markup isn't in the DOM yet */
  const rootEl = document.querySelector('.nalu-booking');
  if (!rootEl) return false;

  /* 2-4. Core mount. `booted` is latched ONLY after this fully succeeds.
   *      Previously it was set true up front, so a throw mid-mount (a transient
   *      DOM/dependency hiccup on a fresh or long-stale load) left booted=true
   *      with a half-wired form — and the guard above then blocked it from ever
   *      re-booting, so the modal was stuck until a manual page refresh. On
   *      failure we reset and return false so the next interaction retries clean. */
  let manage;
  try {
    /* Create router with a safe handler (it may fire before booking is created) */
    let booking; // declare first so the callback can see it
    const router = new HashRouter(state => {
      if (booking && typeof booking.syncWithState === 'function') {
        booking.syncWithState(state);
      }
    });

    /* Mount BookingForm and pass router in */
    booking = new BookingForm(rootEl, router);
    booking.init();

    // Wire modal UX
    NaluBookingUx.bindAll(booking);

    /* Mount the manage-reservation flow as a sibling of the booking
     * funnel. It lives inside the same modal shell but owns its own
     * panels and state machine. */
    manage = new ManageReservation();
    manage.init();
    manage.bind(booking);
    // Expose for the BookingForm close handler to route into when the
    // shared X button is clicked while manage mode is active.
    window.NaluManageReservation = manage;
  } catch (err) {
    booted = false;
    window.NaluManageReservation = undefined;
    naluDebug('Nalu booking boot failed:', err);
    return false;
  }

  booted = true;

  /* Auto-open + per-button manage wiring is best-effort: the form is already
   * mounted above, so a failure here must NOT un-boot it (the user can still
   * click a trigger to open). Wrapped so a malformed deep-link param can't
   * strand the whole modal. */
  try {

  /* 5. Manage-mode auto-open when the page was loaded with ?manageres=.
   *    Strip the param on capture so the bearer-ish code doesn't linger in
   *    browser history or get bookmarked/shared. An empty/malformed code
   *    lands on the manual-entry step (manageOpen=true, manageCode=''). */
  const manageOpen = !!(window.naluBookingVars && window.naluBookingVars.manageOpen);
  const manageCode = (window.naluBookingVars && window.naluBookingVars.manageCode) || '';
  if (manageOpen) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('manageres');
      const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState(null, '', cleaned);
    } catch (e) {
      // URL parsing failures shouldn't block opening the modal.
      console.warn('Failed to strip ?manageres= from URL', e);
    }
    manage.open(manageCode);
  }

  /* 6. New-booking deep-link auto-open when the page was loaded with a booking
   *    link. Two forms (PHP sets bookOpen for either):
   *      - ?mak_booking=1     → bare open on Step-1, no boat pinned (bookTourId 0).
   *      - ?tour_id=<id>      → open on Step-1 with that boat preselected/pinned
   *        (&tour_name=<name>)  on Step-2 — the URL equivalent of a data-tour-id
   *                             trigger button.
   *    Both route through the same per-button preselect path via a synthetic
   *    trigger, so Step-2 pinning + the "…on the {boat}" heading work identically
   *    when an id is present (and are simply skipped when it isn't). For surfaces
   *    that can only emit a plain link (e.g. a Google Business Profile activity
   *    URL). Strip the params on capture so a refresh doesn't reopen and the link
   *    doesn't linger in history. Skipped when manage mode already claimed this
   *    load (manageres wins). */
  const bookOpen   = !!(window.naluBookingVars && window.naluBookingVars.bookOpen);
  const bookTourId = (window.naluBookingVars && String(window.naluBookingVars.bookTourId || '').trim()) || '';
  const bookIntent = (window.naluBookingVars && String(window.naluBookingVars.bookIntent || '').trim()) || '';
  if (!manageOpen && bookOpen) {
    const bookTourName = (window.naluBookingVars && window.naluBookingVars.bookTourName) || '';
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('mak_booking');
      url.searchParams.delete('tour_id');
      url.searchParams.delete('tour_name');
      url.searchParams.delete('intent');
      const cleaned = url.pathname + (url.search ? url.search : '') + url.hash;
      window.history.replaceState(null, '', cleaned);
    } catch (e) {
      // URL parsing failures shouldn't block opening the modal.
      console.warn('Failed to strip booking deep-link params from URL', e);
    }
    // Synthetic trigger reuses NaluBookingUx.openModal's data-* preselect path.
    // With no tour id (bare mak_booking open) the dataset is empty and openModal
    // clears any stale pin → plain Step-1, same as a "Book Now" button. An
    // intent rides alongside (it biases Step-2 ordering, not a single pin).
    const dataset = {};
    if (bookTourId) { dataset.tourId = bookTourId; dataset.tourName = bookTourName; }
    if (bookIntent) { dataset.intent = bookIntent; }
    NaluBookingUx.openModal?.({ dataset });
  }

  /* Manage-mode click triggers ([data-nalu-manage=open] / .btn-nalu-manage).
   * Direct per-button wiring (idempotent); the delegated handler below hands
   * off to these once booted. */
  document.querySelectorAll('[data-nalu-manage=open], .btn-nalu-manage').forEach((btn) => {
    if (btn._wiredNaluManageOpen) return;
    btn._wiredNaluManageOpen = true;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      manage.open('');
    });
  });

  } catch (err) {
    naluDebug('Nalu booking auto-open failed:', err);
  }

  return true;
}

// ────────────────────────────────────────────────────────────────
// Boot loader overlay
//
// The in-modal spinner (#naluLoader) lives INSIDE the lazily-fetched markup, so
// it can't give feedback during the very first fetch — the window where the user
// taps "Book Now" and nothing visible happens (so they tap again). This tiny
// standalone overlay reuses the same global `.nalu-loader` CSS (enqueued on page
// load, independent of the modal markup) to cover exactly that gap. The short
// show-delay keeps it from flashing when the markup is already warm/cached and
// the modal opens effectively instantly.
// ────────────────────────────────────────────────────────────────
let bootLoaderEl    = null;
let bootLoaderTimer = null;

function showBootLoader () {
  if (bootLoaderTimer) return; // already pending
  bootLoaderTimer = window.setTimeout(() => {
    bootLoaderTimer = null;
    if (!bootLoaderEl) {
      bootLoaderEl = document.createElement('div');
      bootLoaderEl.className = 'nalu-loader';
      bootLoaderEl.id = 'naluBootLoader';
      bootLoaderEl.setAttribute('role', 'status');
      bootLoaderEl.setAttribute('aria-live', 'polite');
      bootLoaderEl.innerHTML =
        '<div class="nalu-loader__wrapper">' +
          '<div class="nalu-loader__text">Loading…</div>' +
          '<div class="nalu-loader__spinner"></div>' +
        '</div>';
      document.body.appendChild(bootLoaderEl);
    }
    void bootLoaderEl.offsetWidth; // reflow so the opacity transition runs
    bootLoaderEl.classList.add('is-active');
  }, 120);
}

function hideBootLoader () {
  if (bootLoaderTimer) { window.clearTimeout(bootLoaderTimer); bootLoaderTimer = null; }
  if (bootLoaderEl) bootLoaderEl.classList.remove('is-active');
}

// Swap the boot overlay into an error state with a retry affordance. A failed
// lazy-load used to be a silent dead-end (.catch(()=>{})) that only a manual page
// refresh cleared; this makes it recoverable in place. `retry` re-runs the
// original open attempt. Button styling is inline so this needs no extra CSS.
function showBootError (retry) {
  if (bootLoaderTimer) { window.clearTimeout(bootLoaderTimer); bootLoaderTimer = null; }
  if (!bootLoaderEl) {
    bootLoaderEl = document.createElement('div');
    bootLoaderEl.className = 'nalu-loader';
    bootLoaderEl.id = 'naluBootLoader';
    document.body.appendChild(bootLoaderEl);
  }
  bootLoaderEl.setAttribute('role', 'alert');
  bootLoaderEl.setAttribute('aria-live', 'assertive');
  bootLoaderEl.innerHTML =
    '<div class="nalu-loader__wrapper" style="text-align:center;max-width:22rem">' +
      '<div class="nalu-loader__text">Sorry — we couldn’t load the booking form.</div>' +
      '<button type="button" id="naluBootRetry" ' +
        'style="margin-top:.85rem;padding:.5rem 1.5rem;border:0;border-radius:9999px;' +
        'background:#1f2937;color:#fff;font:inherit;font-weight:600;cursor:pointer">' +
        'Try again</button>' +
    '</div>';
  void bootLoaderEl.offsetWidth; // reflow so the opacity transition runs
  bootLoaderEl.classList.add('is-active');
  const btn = document.getElementById('naluBootRetry');
  if (btn) btn.addEventListener('click', () => { hideBootLoader(); retry(); }, { once: true });
}

/**
 * One modal-markup fetch with a given nonce. A 403 is tagged as an auth failure
 * (err.authFailure) so the caller can refresh the nonce and retry; any other
 * non-OK status is fatal.
 */
function fetchModalMarkup (nonce) {
  const ajaxUrl = (window.naluBookingVars || {}).ajax_url;
  const body = new URLSearchParams();
  body.set('action', 'nalu_render_modal');
  body.set('security', nonce);

  return fetch(ajaxUrl, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body: body.toString(),
  }).then((res) => {
    if (res.status === 403) { const e = new Error('nonce rejected'); e.authFailure = true; throw e; }
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return res.text();
  });
}

/**
 * Fetch the modal markup once, inject it, and boot. Returns a cached promise so
 * concurrent trigger clicks never double-fetch. If the markup is already in the
 * DOM (inline shortcode), this just boots without a network call. On failure the
 * cached promise is cleared so a later interaction can retry.
 *
 * A statically-cached (WP Rocket) or long-open page carries a nonce that ages
 * out of validity; the first 403 triggers a one-shot nonce refresh + retry so
 * the modal still opens (and the refreshed nonce re-arms the rest of the flow).
 */
function ensureMarkupLoaded () {
  if (markupPromise) return markupPromise;

  // Inline shortcode path — markup already on the page.
  if (document.querySelector('.nalu-booking')) {
    if (bootBooking()) {
      markupPromise = Promise.resolve();
      return markupPromise;
    }
    // Don't cache the failure — let a later interaction retry the mount.
    return Promise.reject(new Error('Nalu booking boot failed'));
  }

  const vars    = window.naluBookingVars || {};
  const ajaxUrl = vars.ajax_url;
  const nonce   = vars.security;
  if (!ajaxUrl || !nonce) {
    naluDebug('Nalu: cannot lazy-load modal — missing ajax_url/nonce');
    return Promise.reject(new Error('Nalu booking config missing'));
  }

  const inject = (html) => {
    const trimmed = (html || '').trim();
    if (!trimmed) throw new Error('empty modal markup');
    const tpl = document.createElement('template');
    tpl.innerHTML = trimmed;
    document.body.appendChild(tpl.content);
    if (!bootBooking()) throw new Error('Nalu booking boot failed');
  };

  markupPromise = fetchModalMarkup(nonce)
    .catch((err) => {
      // Dead nonce on a stale/cached page → mint a fresh one and retry once.
      if (!err || !err.authFailure) throw err;
      return refreshNonce().then((fresh) => {
        if (!fresh) throw err;
        return fetchModalMarkup(fresh);
      });
    })
    .then(inject)
    .catch((err) => {
      markupPromise = null; // allow a later interaction to retry
      naluDebug('Nalu modal lazy-load failed:', err);
      throw err;
    });

  return markupPromise;
}

// ────────────────────────────────────────────────────────────────
// DOM-ready boot
// ────────────────────────────────────────────────────────────────
$(function () {
  /* Global Cosmetics — dropdown toggles are delegated on `document`, so this
   * one call covers modal markup injected later. */
  initDropdownToggles();
  document.documentElement.classList.add('nalu-ready');

  /* Expose UX helper so BookingForm can call window.NaluBookingUx.hardModalClose() */
  window.NaluBookingUx = NaluBookingUx;

  /* Inline shortcode path: the markup is already on the page → boot now. */
  if (document.querySelector('.nalu-booking')) {
    bootBooking();
  }

  /* Delegated trigger handler for the lazy path. Stays bound for the page
   * lifetime; once booted it defers to the per-button handlers that
   * bindOpenButtons() / the manage wiring added during boot (except for
   * triggers added to the DOM after boot, which it still opens). */
  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!target || typeof target.closest !== 'function') return;

    const bookBtn = target.closest('[data-nalu-modal=open], .btn-nalu-modal');
    if (bookBtn) {
      // Already booted and this button is wired → let its own handler open it.
      if (booted && bookBtn._wiredNaluOpen) return;
      e.preventDefault();
      const openBooking = () => {
        showBootLoader();
        ensureMarkupLoaded()
          .then(() => { window.NaluBookingUx.openModal?.(bookBtn); hideBootLoader(); })
          .catch(() => { showBootError(openBooking); });
      };
      openBooking();
      return;
    }

    const manageBtn = target.closest('[data-nalu-manage=open], .btn-nalu-manage');
    if (manageBtn) {
      if (booted && manageBtn._wiredNaluManageOpen) return;
      e.preventDefault();
      const openManage = () => {
        showBootLoader();
        ensureMarkupLoaded()
          .then(() => { window.NaluManageReservation?.open?.(''); hideBootLoader(); })
          .catch(() => { showBootError(openManage); });
      };
      openManage();
    }
  });

  /* Auto-open when the page loaded with ?manageres= (manage mode) or a boat
   * deep link ?tour_id= (new booking, boat preselected). The actual open +
   * URL-strip lives in bootBooking(); we just make sure the markup is fetched
   * so boot can run. */
  if (window.naluBookingVars && (window.naluBookingVars.manageOpen || window.naluBookingVars.bookOpen)) {
    ensureMarkupLoaded().catch(() => {});
  }

  /* Warm the markup ahead of the first trigger click so it opens instantly
   * instead of waiting on the fetch. Single cached request (ensureMarkupLoaded
   * guards re-entry); no-op when the markup is already present (inline
   * shortcode) or the manage path above already started the fetch.
   *
   * Primary trigger is the visitor's FIRST interaction — pointerdown / touchstart
   * / scroll / keydown. The booking funnel is the page's primary action, but the
   * rest is informational, so we don't burn a backend request on pure bounce
   * traffic (load-and-leave with no interaction → never warmed). On mobile there
   * is no hover, but the first scroll/tap still lands well before the visitor
   * reaches and presses "Book Now" (they scroll to find it), so the markup is
   * usually already fetched by click time. The boot-loader overlay backstops
   * anyone who still out-races the warm. An idle fallback covers the lingering
   * reader who never interacts, so behaviour is never slower than before. */
  if (window.naluBookingVars) {
    let warmed = false;
    const warm = () => {
      if (warmed) return;          // idempotent across all the triggers below
      warmed = true;
      ensureMarkupLoaded().catch(() => {});
    };

    ['pointerdown', 'touchstart', 'scroll', 'keydown'].forEach((ev) =>
      window.addEventListener(ev, warm, { once: true, passive: true }));

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warm, { timeout: 3000 });
    } else {
      // Safari < 17 and friends: defer past first paint with a timer.
      window.setTimeout(warm, 1500);
    }
  }
});
